/* eslint-disable no-undef */
/* eslint-disable no-inner-declarations */
import { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { errorCodes, screenDetailTypes } from '../../config/constants';
import { EVENT } from '../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import { getMixpanelProfileName, getMixpanelProfileProperties } from '../../contexts/MixpanelHelper';
import h, { getNewContentRating, sanitizeText } from '../../helpers/common';
import url from '../../helpers/url';
import user from '../../helpers/user';
import { maxLength, required, validAlphanumeric } from '../../helpers/validator';
import { editProfileDone, removeProfile, updateProfile } from '../../store/reducers/profileSlice';
import { __ } from '../../translations';
import ChangeToKidModePopup from '../elements/ChangeToKidModePopup';
import ConfirmPopup from '../elements/ConfirmPopup';
import LanguagePopup from '../elements/LanguagePopup';
import PasswordPopup from '../elements/PasswordPopup';
import SelectProfileLoader from '../elements/SelectProfileLoader';
import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    FlexWrapper,
    InputField,
    RangeSlider,
    Switch,
    Text,
    toast,
} from '@intigral/shared';
import withResponsiveStyles from '@intigral/shared/components/hocs/ResponiseStyles/withResponsiveStyles';
import withPresetWidth from '@intigral/shared/lib/components/hocs/withPresetWidth/withPresetWidth';
import _ from 'lodash';
import Link from 'next/link';
import { IStore } from '../../interfaces/StoreTypes';
import { useQueryClient } from '@tanstack/react-query';
import { useProfileList } from '../../utils/useUserProfile';
import { getUserInfo } from '../../utils/user';
import { Carousel2, HeaderWcp } from '@intigral/ui';
import { Link as componentLink } from 'react-router-dom';
import { SectionHeaderWithPagination } from '@intigral/ui/components/SectionHeaderWithPagination';
import { AvatarPicture } from '@intigral/domain/lib/entity/interface/iProfile';
import { getSelectedProfileAvatars } from '../../helpers/profile';
import useProfileAvatars from '../../hooks/useProfileAvatars';
import { Modal } from './Modal';
import { useConfigurationSections } from '../../hooks/useConfiguration';

const InputStyle = withResponsiveStyles(InputField);
const LinkStyle = withResponsiveStyles(Link);
const BoxWidth = withPresetWidth(Box);

type CurrentProfileType = {
    autoPlayNextEpisode: boolean;
    avatar: string;
    avatarColor?: string;
    avatarId?: string;
    avatarIndex: number;
    coldStart: number;
    createdOn: number;
    default: boolean;
    defaultAudioLang: string;
    defaultSubTitleLang: string;
    kidsDefault: boolean;
    kidsMode: boolean;
    newParentalControl: string;
    parentalControl: string;
    passwordProtected: boolean;
    profileGuid: string;
    profileName: string;
    profilePinRequired: boolean;
    updatedOn: number;
    parentalLockOn?: boolean;
};

export const EditProfile = (props) => {
    const { selectedProfile, isLoading, errCode, errMess, profile, isFormSubmitted } = useSelector(
        (state: IStore) => state.profile
    );
    const { data: profileList } = useProfileList(getUserInfo());
    // Using React Query instead of Redux for configuration
    const { profile: profileConfig } = useConfigurationSections();
    const profileNameMaxLength = profileConfig?.profileNameMaxLength;

    const [isOpen, setIsOpen] = useState(false);
    const { data: avatarData } = useProfileAvatars();
    const dispatch = useDispatch();

    let currentProfile: CurrentProfileType = {
        autoPlayNextEpisode: false,
        avatar: '',
        avatarId: '',
        avatarColor: '',
        avatarIndex: 0,
        coldStart: 0,
        createdOn: Date.now(),
        default: false,
        defaultAudioLang: 'en',
        defaultSubTitleLang: 'en',
        kidsDefault: false,
        kidsMode: false,
        newParentalControl: '',
        parentalControl: '',
        passwordProtected: false,
        profileGuid: '',
        profileName: '',
        profilePinRequired: false,
        updatedOn: Date.now(),
        parentalLockOn: false,
    };
    const profileNotFound = (
        <section className="watchlist-main">
            <div className="watchlist-block">
                <span className="watchlist-icon">
                    <img src="images/main-watchlist_icon.svg" alt="" />
                </span>
                <span className="watchlist-desc">{__('profile_not_found')}</span>
            </div>
        </section>
    );

    if (url.getQuery('guid')) {
        try {
            const decodeValue = window.atob(url.getQuery('guid'));
            if (decodeValue) {
                currentProfile = _.find(profileList.profiles, (a) => a.profileGuid === atob(url.getQuery('guid')));
                if (typeof currentProfile === 'undefined') {
                    return profileNotFound;
                }
            }
        } catch (e) {
            return profileNotFound;
        }
    }

    const { track }: { track: any } = useContext(MixpanelContext);
    // profile data
    const [profileName, setProfileName] = useState(currentProfile?.profileName);
    const [passwordProtected, togglePasswordProtected] = useReducer(
        (checked) => !checked,
        currentProfile.passwordProtected
    );

    // State to store selected avatar properties
    const [selectedAvatar, setSelectedAvatar] = useState({
        avatarColor: currentProfile.avatarColor,
        avatar: currentProfile.avatar,
        avatarId: currentProfile.avatarId,
    });

    const getProfileAvatar = (selectedAvatar) => {
        return getSelectedProfileAvatars(selectedAvatar, avatarData);
    };
    const [defaultAudioLang, setDefaultAudioLang] = useState(currentProfile.defaultAudioLang);
    const [defaultSubTitleLang, setDefaultSubTitleLang] = useState(currentProfile.defaultSubTitleLang);
    const [autoPlayNextEpisode, toggleAutoPlayNextEpisode] = useReducer(
        (checked) => !checked,
        currentProfile.autoPlayNextEpisode
    );
    const [kidModeEnable, toggleKidsMode] = useReducer((checked) => !checked, currentProfile.kidsMode ?? false);
    const [ratingValue, setRatingValue] = useState(currentProfile.newParentalControl || currentProfile.parentalControl);
    const [userPassword, setUserPassword] = useState(false);
    // Lock profile with PIN
    const [parentalLockOn, toggleParentalLock] = useReducer((checked: any) => !checked, currentProfile?.parentalLockOn);
    // password popup state
    const [showAudioChoosePopup, setShowAudioChoosePopup] = useState(false);
    const [showLanguageChoosePopup, setShowLanguageChoosePopup] = useState(false);
    const [showPasswordPopup, setShowPasswordPopup] = useState(false);
    const [showChangeToKidModePopup, setShowChangeToKidModePopup] = useState(false);
    const [callValidatePassword, setCallValidatePassword] = useState(false);
    // confirm delete popup state
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [confirmDeleteProfile, setConfirmDeleteProfile] = useState(false);
    // loadingProfile popup states
    const [loadingProfile, setLoadingProfile] = useState<any>(null);
    // back & redirect route
    const navigate = useNavigate();
    const [backPageRoute, setBackPageRoute] = useState(url.getDetailsPage(screenDetailTypes.PROFILE_SELECT));

    const [lockFormActions, setLockFormActions] = useState(false);

    // actions results
    const [passwordValidated, setPasswordValidated] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [failedAdd, setFailedAdd] = useState(false);
    const [formAction, setFormAction] = useState('edit');
    const [deleteActionStatus, setDeleteActionSubmitted] = useState(false);
    const [disableSaveButton, setDisableSaveButton] = useState(true);
    const queryClient = useQueryClient();
    // Persist the initial values
    const initialStates = useRef({
        profileName,
        passwordProtected,
        defaultAudioLang,
        defaultSubTitleLang,
        autoPlayNextEpisode,
        kidModeEnable,
        ratingValue,
        parentalLockOn,
        selectedAvatar,
    }).current;

    const initialKidsModeStatus = currentProfile.kidsMode;

    // change rating based on mode
    useEffect(() => {
        if (initialKidsModeStatus !== kidModeEnable) {
            setRatingValue(kidModeEnable ? 'PG' : ratingValue || 'R18');

            if (!currentProfile.kidsMode && kidModeEnable) {
                setShowChangeToKidModePopup(true);
            }
        }

        if (kidModeEnable && passwordProtected) {
            togglePasswordProtected();
        }
    }, [kidModeEnable]);

    useEffect(() => {
        if (failedAdd && errCode) {
            track(EVENT.PROFILE.PROFILES_EDIT_FAILED, { errCode: errCode, errMess: errMess });
            toast(__(`err_msg_${errCode}`), 'error');
        }
    }, [failedAdd, errCode]);

    const navigation = (route: string) => navigate(route);
    // profile actions
    const updateProfileData = useCallback(
        (profileGuid, profileData, kidsMode, currKidsMode, userPassword, currentProfile) =>
            dispatch(
                updateProfile({
                    params: { profileGuid, profileData, kidsMode, currKidsMode, userPassword, currentProfile },
                    navigation,
                })
            ),
        [dispatch]
    );
    const removeProfileData = useCallback(
        (profile, userPassword) => dispatch(removeProfile({ profile, userPassword })),
        [dispatch]
    );

    // errors states
    const [profileNameError, setProfileNameError] = useState('');

    //
    const profileNameRef = useRef<HTMLDivElement | null>(null);
    const scrollToRef = (ref: { current: HTMLElement | null }) => {
        if (!ref?.current) return;
        window.scroll({ top: ref.current.offsetTop, behavior: 'smooth' });
    };

    const validateForm = () => {
        if (!required(profileName)) {
            setProfileNameError(__('err_msg_empty_profile_name'));
            return false;
        }

        if (!maxLength(profileNameMaxLength)(profileName)) {
            setProfileNameError(__('err_msg_max_profile_name'));
            return false;
        }

        if (!validAlphanumeric(profileName)) {
            setProfileNameError(__(`err_msg_invalid_profile_name`));
            return false;
        }
        // profile name already used
        if (
            profileName.toLowerCase() !== currentProfile.profileName.toLowerCase() &&
            _.includes(
                _.map(profileList.profiles, (i) => (i['profileName'] || '').toLowerCase()),
                profileName.toLowerCase()
            )
        ) {
            setProfileNameError(__(`err_msg_${errorCodes.PROFILE_NAME_EXISTS}`));
            return false;
        }

        return true;
    };

    const editProfile = () => {
        if (validateForm()) {
            // if user does not change the passwordProtected or parentalControl without asking for password
            if (
                (!passwordProtected &&
                    passwordProtected === currentProfile.passwordProtected &&
                    (ratingValue === currentProfile.parentalControl ||
                        ratingValue === currentProfile.newParentalControl)) ||
                passwordValidated
            ) {
                const profileData = {
                    profileName: sanitizeText(profileName.trim()),
                    profileSettings: {
                        //avatar: selectedAvatar.avatar,
                        avatarId: selectedAvatar.avatarId,
                        newParentalControl: ratingValue,
                        defaultAudioLang: defaultAudioLang,
                        passwordProtected: passwordProtected,
                        defaultSubTitleLang: defaultSubTitleLang,
                        autoPlayNextEpisode: autoPlayNextEpisode,
                        parentalLockOn:
                            kidModeEnable && (h.isAccountRetrievalFeatureEnabled() ? parentalLockOn : false),
                    },
                };

                if (initialKidsModeStatus !== kidModeEnable) {
                    profileData['kidsMode'] = kidModeEnable;
                }
                updateProfileData(
                    currentProfile.profileGuid,
                    profileData,
                    initialKidsModeStatus !== kidModeEnable ? kidModeEnable : null,
                    currentProfile.kidsMode,
                    userPassword,
                    currentProfile
                );
                setFormSubmitted(true);
            } else if (
                !currentProfile.passwordProtected &&
                (ratingValue !== currentProfile.parentalControl || ratingValue !== currentProfile.newParentalControl)
            ) {
                setCallValidatePassword(true);
                setShowPasswordPopup(true);
            } else {
                setShowPasswordPopup(true);
            }
        } else {
            scrollToRef(profileNameRef);
        }
    };

    const deleteProfile = () => {
        setFormAction('delete');
        if ((!currentProfile.passwordProtected && confirmDeleteProfile) || passwordValidated) {
            removeProfileData(currentProfile, userPassword);
            setFormSubmitted(true);

            // setDeleteActionSubmitted(true);
        } else if (!currentProfile.passwordProtected && !confirmDeleteProfile) {
            setShowConfirmPopup(true);
        } else {
            setShowPasswordPopup(true);
        }
    };
    useEffect(() => {
        if (selectedProfile) {
            track(EVENT.PROFILE.PROFILES_EDIT_VIEW, getMixpanelProfileName(currentProfile.profileName));
        }
    }, [selectedProfile]);

    useEffect(() => {
        if (passwordValidated || confirmDeleteProfile) {
            setShowPasswordPopup(false);
            setPasswordValidated(false);
            if (formAction === 'delete') {
                setConfirmDeleteProfile(false);
                deleteProfile();
            } else {
                editProfile();
            }
        }
    }, [passwordValidated, confirmDeleteProfile]);
    const tab = 1;

    useEffect(() => {
        if (isFormSubmitted && !isLoading) {
            if (errCode) {
                setFailedAdd(true);
            } else {
                setLockFormActions(true);
                setFailedAdd(false);
                if (formAction === 'delete') {
                    track(EVENT.PROFILE.PROFILES_EDIT_DELETE, getMixpanelProfileName(profileName));
                } else {
                    track(
                        EVENT.PROFILE.PROFILES_EDIT_SUCCESS,
                        getMixpanelProfileProperties({ ...profile, kidsMode: kidModeEnable ? true : false })
                    );
                    track(EVENT.PROFILE.PROFILES_EDIT_AVATAR_COMPLETED, {
                        ...getMixpanelProfileProperties({ ...profile, kidsMode: kidModeEnable }),
                        'Avatar ID': selectedAvatar.avatarId,
                    });
                }
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                dispatch(editProfileDone(selectedProfile));
                // redirect to profile setting page
                navigate(url.route(backPageRoute));
            }
            setLoadingProfile(false);
            setUserPassword(false);
        } else if (isLoading) {
            setLoadingProfile({ profileName: profileName, avatar: currentProfile.avatar });
        }
        queryClient.invalidateQueries({ queryKey: ['profiles'] });
    }, [errCode, isLoading, formSubmitted, deleteActionStatus, isFormSubmitted]);

    // Check if profile states is updated
    useEffect(() => {
        // Save button will be disabled until there are changes made.
        setDisableSaveButton(
            profileName == initialStates.profileName &&
                passwordProtected == initialStates.passwordProtected &&
                defaultAudioLang == initialStates.defaultAudioLang &&
                defaultSubTitleLang == initialStates.defaultSubTitleLang &&
                autoPlayNextEpisode == initialStates.autoPlayNextEpisode &&
                kidModeEnable == initialStates.kidModeEnable &&
                ratingValue == initialStates.ratingValue &&
                parentalLockOn == initialStates.parentalLockOn &&
                selectedAvatar.avatarId == initialStates.selectedAvatar.avatarId
        );
    }, [
        profileName,
        passwordProtected,
        defaultAudioLang,
        defaultSubTitleLang,
        autoPlayNextEpisode,
        kidModeEnable,
        ratingValue,
        parentalLockOn,
        selectedAvatar,
    ]);

    const handleAvatarClick = (avatar: AvatarPicture) => {
        // setSelectedAvatar(avatar); // Update selected avatar
        setSelectedAvatar((prev) => ({ ...prev, avatarId: avatar.avatarId, avatar: avatar.imageUrl }));
        setIsOpen(false); // Close modal after selection
    };

    const handleEditAvatarClick = () => {
        track(EVENT.PROFILE.PROFILES_EDIT_AVATAR, {
            ...getMixpanelProfileProperties({ ...profile, kidsMode: kidModeEnable }),
            'Avatar ID': selectedAvatar.avatarId,
        });
        setIsOpen(true);
    };

    // If the flag (Account retrieval feature) is enabled:
    //      Check if the profile is not locked with parental lock
    // otherwise:
    //      Assume the profile is not locked with parental lock
    const profileIsNotLocked = h.isAccountRetrievalFeatureEnabled() ? !selectedProfile.parentalLockOn : true;

    // Because some localization on B2B is different, for example "Password" should be "Account PIN" on B2B
    // If account type is B2B, Then this will be used to add "b2b_" just before the localization key
    const B2B_localization = user.getUserSegment() === 'B2B' ? 'b2b_' : '';

    const newContentRating = getNewContentRating();

    return (
        <>
            <HeaderWcp
                logoProps={{
                    homeUrl: !selectedProfile?.kidsMode ? '/' : 'kids-home',
                    title: 'STC logo',
                    logoSrc: 'images/big-logo.svg',
                    LinkComponent: Link,
                }}
                menuProps={
                    <Breadcrumbs
                        items={[
                            {
                                linkComponent: componentLink,
                                transformLinkProps: ({ href }) => ({ to: href }),
                                href: `${h.getLang() === 'en' ? '/en/' : '/'}`,
                                text: __('profile_menu_home'),
                            },
                            {
                                linkComponent: 'a',
                                href: user.getMyAccountUrl(),
                                text: __('profile_menu_account'),
                            },
                            {
                                text: currentProfile.profileName,
                            },
                        ]}
                    />
                }
            >
                {currentProfile.kidsMode ? (
                    <Avatar
                        image={getProfileAvatar({ ...selectedAvatar, kidsMode: currentProfile.kidsMode })}
                        dimension="medium"
                        variant="header"
                    />
                ) : (
                    <Avatar
                        image={getProfileAvatar({ ...selectedAvatar, kidsMode: currentProfile.kidsMode })}
                        dimension="medium"
                        name={profileName}
                        variant="header"
                    />
                )}
            </HeaderWcp>
            {currentProfile && (
                <>
                    <div className="container">
                        <BoxWidth
                            theme={{ default: 'none', md: 'dark' }}
                            padding={{ default: '0', md: '6x' }}
                            presetWidth="medium"
                            marginBottom="12x"
                        >
                            <FlexWrapper flexDirection="column" gap="2x">
                                {currentProfile.kidsMode ? (
                                    <Avatar
                                        image={getProfileAvatar({
                                            ...selectedAvatar,
                                            kidsMode: currentProfile.kidsMode,
                                        })}
                                        width="16x"
                                        height="16x"
                                        edit
                                        onClick={() => {
                                            handleEditAvatarClick();
                                        }}
                                    />
                                ) : (
                                    <Avatar
                                        image={getProfileAvatar({
                                            ...selectedAvatar,
                                            kidsMode: currentProfile.kidsMode,
                                        })}
                                        currentRestriction={ratingValue}
                                        width="16x"
                                        height="16x"
                                        edit
                                        onClick={() => {
                                            handleEditAvatarClick();
                                        }}
                                        data-test-id="avatar"
                                    />
                                )}
                                <div ref={profileNameRef}>
                                    <InputField
                                        label={__('manage_profile_profile_name')}
                                        type="text"
                                        name="profile-display-name"
                                        autoComplete="off"
                                        max={profileNameMaxLength}
                                        preIcon="kids"
                                        preIconSize="smaller"
                                        preIconPCSize="smaller"
                                        errorMessage={profileNameError}
                                        value={profileName}
                                        invalid={!!profileNameError}
                                        disabled={!user.isAllowedProfileActions()}
                                        onChange={(e) => {
                                            setProfileName(e.target.value);
                                            setProfileNameError('');
                                        }}
                                        data-test-id="profile-name"
                                    />
                                </div>

                                {!selectedProfile.kidsMode &&
                                    profileIsNotLocked &&
                                    !currentProfile.kidsDefault &&
                                    !currentProfile.default && (
                                        <FlexWrapper justifyContent="between" paddingBottom="2x">
                                            <Text
                                                as="h4"
                                                variant="h4"
                                                margin="0"
                                                alignment="start"
                                                size={{ default: '18' }}
                                            >
                                                {__('manage_kid_mode_enable')}
                                            </Text>
                                            <Switch
                                                data-test-id="kidsmode-toogle-btn"
                                                checked={kidModeEnable}
                                                onChange={() => {
                                                    const newKidsMode = !kidModeEnable;
                                                    toggleKidsMode();

                                                    //Reset avatar based on new mode
                                                    if (newKidsMode) {
                                                        const defaultAvatar = avatarData?.find(
                                                            (group) =>
                                                                group.id === 'KIDS_AVATARS' ||
                                                                group.id === 'KIDS_LITTLE_LEGENDS'
                                                        )?.avatarPictures?.[0];
                                                        if (defaultAvatar?.imageUrl && defaultAvatar?.avatarId)
                                                            setSelectedAvatar((prev) => ({
                                                                ...prev,
                                                                avatar: defaultAvatar.imageUrl,
                                                                avatarId: defaultAvatar.avatarId,
                                                            }));
                                                    } else {
                                                        setSelectedAvatar((prev) => ({
                                                            ...prev,
                                                            avatar: '',
                                                            avatarId: '',
                                                        }));
                                                    }
                                                }}
                                            />
                                        </FlexWrapper>
                                    )}

                                {!kidModeEnable && profileIsNotLocked && (
                                    <RangeSlider
                                        labels={newContentRating}
                                        text={newContentRating?.map((level) =>
                                            __(`parental_control_new_level_${level}_desc`)
                                        )}
                                        checked={newContentRating?.indexOf(ratingValue || 'R18')}
                                        onChangeHandler={(value) => setRatingValue(newContentRating[value])}
                                        data-test-id="parental-rating-field"
                                    />
                                )}

                                {kidModeEnable &&
                                    h.isAccountRetrievalFeatureEnabled() &&
                                    !selectedProfile.kidsMode &&
                                    profileIsNotLocked && (
                                        <InputStyle
                                            margin="0"
                                            placeholder={__('profile_enable_parental_lock')}
                                            type="text"
                                            preIcon="lock"
                                            preIconSize="smaller"
                                            preIconPCSize="smaller"
                                            disabled
                                            cta={
                                                <Switch
                                                    onChange={() => toggleParentalLock()}
                                                    checked={parentalLockOn}
                                                />
                                            }
                                            value={__(parentalLockOn ? 'manage_profile_on' : 'manage_profile_off')}
                                            data-test-id="parental-lock"
                                        />
                                    )}

                                {!selectedProfile.kidsMode && profileIsNotLocked && !kidModeEnable && (
                                    <InputStyle
                                        margin="0"
                                        placeholder={__('manage_profile_password_protected')}
                                        type="text"
                                        preIcon="lock"
                                        preIconSize="smaller"
                                        preIconPCSize="smaller"
                                        disabled
                                        cta={<Switch onChange={togglePasswordProtected} checked={passwordProtected} />}
                                        value={__(passwordProtected ? 'manage_profile_on' : 'manage_profile_off')}
                                        data-test-id="profile-lock"
                                    />
                                )}

                                {!selectedProfile.kidsMode && profileIsNotLocked && (
                                    <InputStyle
                                        margin="0"
                                        label={__('manage_profile_default_audio_language')}
                                        type="text"
                                        preIcon="volume-low"
                                        preIconSize="smaller"
                                        preIconPCSize="smaller"
                                        cta={__('manage_profile_change')}
                                        ctaLinkOnClick={(e) => {
                                            e.preventDefault();
                                            setShowAudioChoosePopup(true);
                                        }}
                                        disabled
                                        value={__(`manage_profile_${defaultAudioLang}`)}
                                        data-test-id="audio-field"
                                    />
                                )}

                                {!selectedProfile.kidsMode && profileIsNotLocked && (
                                    <InputStyle
                                        margin="0"
                                        label={__('manage_profile_default_subtitles_language')}
                                        type="text"
                                        preIcon="globe"
                                        preIconSize="smaller"
                                        preIconPCSize="smaller"
                                        cta={__('manage_profile_change')}
                                        ctaLinkOnClick={(e) => {
                                            e.preventDefault();
                                            setShowLanguageChoosePopup(true);
                                        }}
                                        disabled
                                        value={__(`manage_profile_${defaultSubTitleLang}`)}
                                        data-test-id="subtitle-field"
                                    />
                                )}

                                {!selectedProfile.kidsMode && profileIsNotLocked && (
                                    <InputStyle
                                        margin="0"
                                        placeholder={__('manage_profile_autoplay_next_episode')}
                                        type="text"
                                        preIcon="play"
                                        preIconSize="smaller"
                                        preIconPCSize="smaller"
                                        disabled
                                        cta={
                                            <Switch
                                                onChange={toggleAutoPlayNextEpisode}
                                                checked={autoPlayNextEpisode}
                                            />
                                        }
                                        value={__(autoPlayNextEpisode ? 'manage_profile_on' : 'manage_profile_off')}
                                        data-test-id="autoplay-field"
                                    />
                                )}

                                <FlexWrapper
                                    direction={{ default: 'column', md: 'row' }}
                                    gap="3x"
                                    itemCount={2}
                                    paddingTop="3x"
                                    className="profile-save"
                                >
                                    <Button
                                        data-test-id="cancel-btn"
                                        onClick={() => {
                                            navigate(url.route(backPageRoute));
                                        }}
                                    >
                                        {__('manage_profile_cancel')}
                                    </Button>
                                    <Button
                                        primary
                                        disabled={lockFormActions || disableSaveButton}
                                        onClick={editProfile}
                                        data-test-id="save-btn"
                                    >
                                        {__('manage_profile_save')}
                                    </Button>
                                </FlexWrapper>
                                {selectedProfile.profileGuid !== currentProfile.profileGuid && (
                                    <FlexWrapper>
                                        <br />
                                        <LinkStyle
                                            href="/"
                                            className="primary-brand"
                                            placement="center"
                                            data-test-id="delete-btn"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                deleteProfile();
                                            }}
                                        >
                                            {__('manage_profile_delete')}
                                        </LinkStyle>
                                    </FlexWrapper>
                                )}
                            </FlexWrapper>
                        </BoxWidth>
                    </div>

                    <LanguagePopup
                        selectedLanguage={defaultAudioLang}
                        showPopup={showAudioChoosePopup}
                        onSelect={(lang) => setDefaultAudioLang(lang)}
                        onClose={() => setShowAudioChoosePopup(false)}
                    />
                    <LanguagePopup
                        selectedLanguage={defaultSubTitleLang}
                        showPopup={showLanguageChoosePopup}
                        onSelect={(lang) => setDefaultSubTitleLang(lang)}
                        onClose={() => setShowLanguageChoosePopup(false)}
                    />

                    {showPasswordPopup && (
                        <PasswordPopup
                            showPopup={showPasswordPopup}
                            description={__(
                                B2B_localization +
                                    `password_popup_description_${
                                        formAction === 'delete' ? 'deleteProfile' : 'editProfile'
                                    }`
                            )}
                            onClose={() => setShowPasswordPopup(false)}
                            onSubmit={(password) => {
                                setUserPassword(password);
                                setPasswordValidated(true);
                            }}
                            onSuccess={() => setPasswordValidated(true)}
                            callValidatePassword={callValidatePassword}
                        />
                    )}
                    <SelectProfileLoader loadingProfile={loadingProfile} autoCloseLoading={false} />
                    <ConfirmPopup
                        showPopup={showConfirmPopup}
                        onClose={(confirmed) => {
                            setConfirmDeleteProfile(confirmed);
                            setShowConfirmPopup(false);
                        }}
                    />
                    <ChangeToKidModePopup
                        showPopup={showChangeToKidModePopup}
                        onClose={() => {
                            setShowChangeToKidModePopup(false);
                        }}
                    />

                    {isOpen && (
                        <EditProfilePopup
                            isOpen={isOpen}
                            setIsOpen={setIsOpen}
                            handleAvatarClick={handleAvatarClick}
                            currentProfile={currentProfile}
                            avatarData={avatarData}
                            kidModeEnable={kidModeEnable}
                        />
                    )}
                </>
            )}
        </>
    );
};

const EditProfilePopup = ({ isOpen, setIsOpen, currentProfile, handleAvatarClick, avatarData, kidModeEnable }) => {
    return (
        <Modal useFlexLayout={false} open={isOpen} onOpenChange={() => setIsOpen(!open)}>
            <Text as="h2" variant="h2" alignment="start">
                {__('choose_profile_avatar')}
            </Text>
            <FlexWrapper flexDirection="column" gap="3x">
                {avatarData &&
                    avatarData.length > 0 &&
                    avatarData
                        ?.filter((group) => group.kidsMode === kidModeEnable)
                        .map((group, groupIndex) => {
                            const isEn = h.getLang() === 'en';
                            const sectionTitle = isEn ? group.titleEn : group.titleAr;
                            // Case Handling: if no Avatar pictures for any group, no need to show the group title
                            if (!group.avatarPictures || group.avatarPictures.length === 0) {
                                return null;
                            }

                            return (
                                <div key={group.id}>
                                    <SectionHeaderWithPagination
                                        title={sectionTitle}
                                        className={`paging-${groupIndex}`}
                                        mobileHidden={false}
                                    />
                                    <Carousel2
                                        swiperSettings={{
                                            pagination: {
                                                el: `.paging-${groupIndex}`,
                                                clickable: true,
                                            },
                                            slidesPerView: 3,
                                            slidesPerGroup: 1,
                                            breakpoints: {
                                                560: {
                                                    slidesPerView: 4,
                                                },
                                                720: {
                                                    slidesPerView: 5,
                                                    slidesPerGroup: 1,
                                                },
                                                800: {
                                                    slidesPerView: 6,
                                                },
                                            },
                                        }}
                                    >
                                        {group.avatarPictures?.map((avatar) => (
                                            <Avatar
                                                key={avatar.avatarId}
                                                image={avatar.imageUrl || 'https://cdn.jawwy.tv/9/avatar-smile.svg'}
                                                color="transparent"
                                                width={{ default: '18x', md: '22x' }}
                                                height={{ default: '18x', md: '22x' }}
                                                onClick={() => handleAvatarClick(avatar)}
                                                data-test-id="avatar-btn"
                                            />
                                        ))}
                                    </Carousel2>
                                </div>
                            );
                        })}
            </FlexWrapper>
        </Modal>
    );
};
